import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { formatNumber } from '@/utils/formatNumber';
import { User } from '@/types/index';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { UserPlus, UserCheck, Star } from 'lucide-react';

// Extended User interface with boosted status and recent media
interface ExtendedUser extends User {
  boosted?: boolean;
  recentMedia?: {
    id: string;
    url: string;
    type: 'image' | 'video';
  }[];
}

interface LikesPreviewProps {
  postId: string;
  likeCount: number;
}

// Sample data for users who liked the post
// In a real app, this would come from an API call
const sampleLikers: ExtendedUser[] = [
  {
    id: '1',
    username: 'johndoe',
    avatar_url: 'https://pixabay.com/get/g0d5e4c1e0c6e2a2f9c0f5e4c4a8c3f4e9f0a0f3c3_640.jpg',
    verified: true,
    boosted: true,
    recentMedia: [
      { id: '1-1', url: 'https://cdn.pixabay.com/photo/2015/04/23/22/00/tree-736885_1280.jpg', type: 'image' },
      { id: '1-2', url: 'https://cdn.pixabay.com/photo/2015/12/01/20/28/road-1072823_1280.jpg', type: 'image' },
      { id: '1-3', url: 'https://cdn.pixabay.com/photo/2015/06/19/21/24/avenue-815297_1280.jpg', type: 'image' },
      { id: '1-4', url: 'https://cdn.pixabay.com/photo/2017/02/01/22/02/mountain-landscape-2031539_1280.jpg', type: 'image' },
      { id: '1-5', url: 'https://cdn.pixabay.com/photo/2014/09/14/18/04/dandelion-445228_1280.jpg', type: 'image' }
    ]
  },
  {
    id: '2',
    username: 'janedoe',
    avatar_url: 'https://pixabay.com/get/g8d2e7c5e6c9e1a2f9c0f5e4c4a8c3f4e9f0a0f3c3_640.jpg',
    verified: false
  },
  {
    id: '3',
    username: 'mikesmith',
    avatar_url: 'https://pixabay.com/get/g3d1e2c8e7c4e0a2f9c0f5e4c4a8c3f4e9f0a0f3c3_640.jpg',
    verified: true,
    boosted: true,
    recentMedia: [
      { id: '3-1', url: 'https://cdn.pixabay.com/photo/2016/11/29/04/19/ocean-1867285_1280.jpg', type: 'image' },
      { id: '3-2', url: 'https://cdn.pixabay.com/photo/2015/07/09/22/45/tree-838667_1280.jpg', type: 'image' },
      { id: '3-3', url: 'https://cdn.pixabay.com/photo/2016/08/11/23/48/mountains-1587287_1280.jpg', type: 'image' },
      { id: '3-4', url: 'https://cdn.pixabay.com/photo/2016/11/14/04/45/elephant-1822636_1280.jpg', type: 'image' },
      { id: '3-5', url: 'https://cdn.pixabay.com/photo/2016/03/27/22/22/fox-1284512_1280.jpg', type: 'image' }
    ]
  },
  {
    id: '4',
    username: 'sarahlee',
    avatar_url: 'https://pixabay.com/get/g5d9e0c2e8c3e5a2f9c0f5e4c4a8c3f4e9f0a0f3c3_640.jpg',
    verified: false
  },
  {
    id: '5',
    username: 'alexjones',
    avatar_url: 'https://pixabay.com/get/g6d4e3c7e2c8e7a2f9c0f5e4c4a8c3f4e9f0a0f3c3_640.jpg',
    verified: false
  },
  {
    id: '6',
    username: 'emilybrown',
    avatar_url: 'https://pixabay.com/get/g7d8e9c1e3c2e6a2f9c0f5e4c4a8c3f4e9f0a0f3c3_640.jpg',
    verified: true,
    boosted: true,
    recentMedia: [
      { id: '6-1', url: 'https://cdn.pixabay.com/photo/2018/01/14/23/12/nature-3082832_1280.jpg', type: 'image' },
      { id: '6-2', url: 'https://cdn.pixabay.com/photo/2015/12/01/20/28/forest-1072828_1280.jpg', type: 'image' },
      { id: '6-3', url: 'https://cdn.pixabay.com/photo/2015/04/23/22/01/mountains-736886_1280.jpg', type: 'image' },
      { id: '6-4', url: 'https://cdn.pixabay.com/photo/2017/05/09/03/46/alberta-2297204_1280.jpg', type: 'image' },
      { id: '6-5', url: 'https://cdn.pixabay.com/photo/2018/08/14/13/23/ocean-3605547_1280.jpg', type: 'image' }
    ]
  },
  {
    id: '7',
    username: 'davidwilson',
    avatar_url: 'https://pixabay.com/get/g9d7e5c4e1c7e4a2f9c0f5e4c4a8c3f4e9f0a0f3c3_640.jpg',
    verified: false
  },
  {
    id: '8',
    username: 'oliviamartin',
    avatar_url: 'https://pixabay.com/get/g0d6e8c9e5c1e3a2f9c0f5e4c4a8c3f4e9f0a0f3c3_640.jpg',
    verified: true
  }
];

export default function LikesPreview({ postId, likeCount }: LikesPreviewProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [likers, setLikers] = useState<ExtendedUser[]>([]);

  // In a real app, fetch likers when the component mounts or when postId changes
  useEffect(() => {
    // This would be an API call in a real app
    // For now, use sample data
    setLikers(sampleLikers);
  }, [postId]);

  // Get preview likers (max 5)
  const previewLikers = likers.slice(0, 5);

  return (
    <>
      {/* Likes Preview */}
      <div
        className="flex items-center px-3 py-2 cursor-pointer border-b border-gray-100 dark:border-gray-800"
        onClick={() => setIsDialogOpen(true)}
      >
        {/* Avatar stack */}
        <div className="flex -space-x-2 mr-2">
          {previewLikers.map((liker) => (
            <Avatar key={liker.id} className="h-5 w-5 border border-white dark:border-gray-800 rounded-full">
              <AvatarImage src={liker.avatar_url} alt={liker.username} />
              <AvatarFallback>
                <img
                  src={`https://ui-avatars.com/api/?name=${liker.username}&background=random&color=fff&size=100`}
                  alt={liker.username}
                  className="h-full w-full object-cover"
                />
              </AvatarFallback>
            </Avatar>
          ))}
        </div>

        {/* Like count */}
        <span className="text-xs text-gray-600 dark:text-gray-300">
          {likeCount > 0 ? `Liked by ${formatNumber(likeCount)} ${likeCount === 1 ? 'person' : 'people'}` : 'No likes yet'}
        </span>
      </div>

      {/* Likes Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="text-center font-semibold">Likes</DialogTitle>

          {/* Scrollable list of likers (no visible scrollbar) */}
          <div className="max-h-[60vh] overflow-y-auto scrollbar-hide">
            {likers.map((liker) => (
              <div key={liker.id} className="flex flex-col py-3 px-1 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8 rounded-full">
                      <AvatarImage src={liker.avatar_url} alt={liker.username} />
                      <AvatarFallback>{liker.username.substring(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <div className="flex items-center">
                        <span className="font-medium">{liker.username}</span>
                        {liker.verified && (
                          <svg className="w-3.5 h-3.5 ml-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                          </svg>
                        )}
                        {liker.boosted && (
                          <Badge className="ml-1 h-4 w-4 bg-transparent p-0">
                            <Star className="h-4 w-4 text-[#ffc11f] fill-[#ffc11f]" />
                          </Badge>
                        )}
                      </div>
                      {liker.boosted && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">Boosted Profile</span>
                      )}
                    </div>
                  </div>
                  <button className="flex items-center justify-center w-7 h-7 bg-white dark:bg-gray-800 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-0">
                    {Math.random() > 0.5 ? (
                      <UserCheck className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    ) : (
                      <UserPlus className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    )}
                  </button>
                </div>

                {/* Recent media thumbnails for boosted users */}
                {liker.boosted && liker.recentMedia && liker.recentMedia.length > 0 && (
                  <div className="grid grid-cols-5 gap-1 mt-1">
                    {liker.recentMedia.slice(0, 5).map((media) => (
                      <div key={media.id} className="aspect-square overflow-hidden rounded-md">
                        <img
                          src={media.url}
                          alt="Recent post"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
